// Desktop Models Manager Class
// Handles business logic for local AI model management via backend API
// Follows the Manager and Component pattern

import { Manager } from "../../../../../core";
import { logger } from "../../../../../core";

export class DesktopModelsManager extends Manager {
  constructor() {
    super();
    this.models = [];
    this.isInitialized = false;
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|desktop|models|init",
        color1: "cyan",
      },
      "Initializing Desktop Models manager states"
    );

    try {
      // Load models from desktop database API
      await this.loadModels();

      this.isInitialized = true;

      logger.log(
        {
          tags: "manager|desktop|models|ready",
          color1: "green",
        },
        `Desktop Models manager initialized with ${this.models.length} models`
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|init",
          color1: "red",
        },
        "Failed to initialize Desktop Models manager:",
        error
      );
      throw error;
    }
  }

  /**
   * Load models from backend
   */
  async loadModels() {
    try {
      if (window.modelsAPI) {
        this.models = await window.modelsAPI.getAll();
      } else {
        this.models = [];
      }
    } catch (error) {
      logger.warn(
        {
          tags: "manager|desktop|models|load",
          color1: "yellow",
        },
        "Failed to load models from backend, starting with empty list:",
        error
      );
      this.models = [];
    }
  }

  /**
   * Save models to database
   */
  async saveModels() {
    try {
      if (window.databaseAPI) {
        const modelsConfig = this.models.map(model => ({
          id: model.id,
          name: model.name,
          description: model.description,
          size: model.size,
          format: model.format,
          parameters: model.parameters,
          contextLength: model.contextLength,
          quantization: model.quantization,
          downloadDate: model.downloadDate,
          isDownloaded: model.isDownloaded,
          isLoaded: model.isLoaded,
          isEnabled: model.isEnabled,
          path: model.path,
          url: model.url
        }));

        await window.databaseAPI.setConfig('desktop-models', modelsConfig);

        logger.log(
          {
            tags: "manager|desktop|models|save",
            color1: "blue",
          },
          `Saved ${modelsConfig.length} models to database`
        );
      }
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|save",
          color1: "red",
        },
        "Failed to save desktop models:",
        error
      );
      throw error;
    }
  }

  /**
   * Add a new model
   */
  async addModel(modelData) {
    try {
      logger.log(
        {
          tags: "manager|desktop|models|add",
          color1: "blue",
        },
        "Adding new model:",
        modelData
      );

      if (window.modelsAPI) {
        const newModel = await window.modelsAPI.create(modelData);
        // Refresh local cache
        await this.loadModels();

        logger.log(
          {
            tags: "manager|desktop|models|add|success",
            color1: "green",
          },
          "Successfully added model:",
          newModel.displayName
        );

        return newModel;
      }
      throw new Error('Models API not available');
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|add",
          color1: "red",
        },
        "Failed to add model:",
        error
      );
      throw error;
    }
  }

  /**
   * Validate whether a local GGUF file exists on disk.
   * Uses backend `models:validate-path` IPC.
   */
  async validateModelPath(pathToValidate, mode = 'file') {
    if (!window.modelsAPI?.validatePath) return false;
    return await window.modelsAPI.validatePath(pathToValidate, mode);
  }

  /**
   * Update a model
   */
  async updateModel(modelId, updates) {
    try {
      if (window.modelsAPI) {
        const updatedModel = await window.modelsAPI.update(modelId, updates);
        // Refresh local cache
        await this.loadModels();

        logger.log(
          {
            tags: "manager|desktop|models|update",
            color1: "blue",
          },
          "Updated model:",
          updatedModel.displayName
        );

        return updatedModel;
      }
      throw new Error('Models API not available');
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|update",
          color1: "red",
        },
        "Failed to update model:",
        error
      );
      throw error;
    }
  }

  /**
   * Configure a model (placeholder for future implementation)
   */
  async configureModel(modelId) {
    try {
      const model = this.models.find(m => m.id === modelId);
      if (!model) {
        throw new Error(`Model ${modelId} not found`);
      }

      logger.log(
        {
          tags: "manager|desktop|models|configure",
          color1: "blue",
        },
        "Configuring model:",
        model.name
      );

      // In a real implementation, this would open a configuration dialog
      // For now, just log the action
      alert(`Configuration for ${model.name} would open here.`);

    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|configure",
          color1: "red",
        },
        "Failed to configure model:",
        error
      );
      throw error;
    }
  }

  /**
   * Delete a model
   */
  async deleteModel(modelId) {
    try {
      if (window.modelsAPI) {
        await window.modelsAPI.delete(modelId);
        // Refresh local cache
        await this.loadModels();

        logger.log(
          {
            tags: "manager|desktop|models|delete",
            color1: "yellow",
          },
          "Successfully deleted model"
        );
      } else {
        throw new Error('Models API not available');
      }
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|delete",
          color1: "red",
        },
        "Failed to delete model:",
        error
      );
      throw error;
    }
  }

  /**
   * Refresh models list (reload from database)
   */
  async refreshModels() {
    try {
      logger.log(
        {
          tags: "manager|desktop|models|refresh",
          color1: "blue",
        },
        "Refreshing models list"
      );

      await this.loadModels();

      logger.log(
        {
          tags: "manager|desktop|models|refresh|success",
          color1: "green",
        },
        `Refreshed models list: ${this.models.length} models`
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|models|refresh",
          color1: "red",
        },
        "Failed to refresh models:",
        error
      );
      throw error;
    }
  }

  /**
   * Get current models list
   */
  getModels() {
    return [...this.models];
  }

  /**
   * Get model by ID
   */
  getModelById(modelId) {
    return this.models.find(model => model.id === modelId);
  }

  /**
   * Get recommended models for the "Discover" tab.
   * These are intentionally curated metadata objects (no download/import logic here).
   */
  async getRecommendedModels() {
    if (!window.modelsAPI?.getRecommendedModels) return [];
    return await window.modelsAPI.getRecommendedModels();
  }

  /**
   * Download a recommended GGUF into `destinationFolder`, then import it.
   */
  async downloadAndImportRecommended(recommendedModel, destinationFolder, color = undefined) {
    if (!window.modelsAPI?.downloadAndImportRecommended) {
      throw new Error('Models API not available');
    }
    return await window.modelsAPI.downloadAndImportRecommended(recommendedModel, destinationFolder, color);
  }

  /**
   * Download an arbitrary GGUF URL (e.g. a Hugging Face file selected from the
   * Discover tab's repo detail view) into `destinationFolder`, then import it.
   *
   * @param {{ displayName: string, downloadUrl: string }} input
   */
  async downloadAndImportUrl(input, destinationFolder, color = undefined) {
    if (!window.modelsAPI?.downloadAndImportUrl) {
      throw new Error('Models API not available');
    }
    return await window.modelsAPI.downloadAndImportUrl(input, destinationFolder, color);
  }

  /**
   * Search the Hugging Face Hub via the desktop backend proxy.
   * Mirrors the huggingface.co/models page filters (skill:
   * huggingface-hub-model-search).
   */
  async hubSearchModels(searchInput = {}) {
    if (!window.modelsAPI?.hubSearchModels) {
      throw new Error('Models API not available');
    }
    return await window.modelsAPI.hubSearchModels(searchInput);
  }

  /**
   * List + classify files in a Hugging Face model repo.
   * Returns { classification: { single, shards, projector }, allFiles, info }.
   */
  async hubListRepoFiles(repoInput) {
    if (!window.modelsAPI?.hubListRepoFiles) {
      throw new Error('Models API not available');
    }
    return await window.modelsAPI.hubListRepoFiles(repoInput);
  }

  /**
   * Extract model name from URL or path
   */
  extractModelName(urlOrPath) {
    try {
      // Extract model name from various URL formats
      const url = new URL(urlOrPath);

      // For Hugging Face URLs like https://huggingface.co/microsoft/DialoGPT-medium
      if (url.hostname === 'huggingface.co') {
        const pathParts = url.pathname.split('/').filter(p => p);
        if (pathParts.length >= 2) {
          return `${pathParts[0]}/${pathParts[1]}`;
        }
      }

      // For file paths, extract filename without extension
      const pathParts = urlOrPath.split(/[/\\]/);
      const filename = pathParts[pathParts.length - 1];
      return filename.replace(/\.(gguf|bin)$/i, '');

    } catch (error) {
      // If it's not a valid URL, treat as path
      const pathParts = urlOrPath.split(/[/\\]/);
      const filename = pathParts[pathParts.length - 1];
      return filename.replace(/\.(gguf|bin)$/i, '') || 'Unknown Model';
    }
  }
}
